import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { defineAbility, Ability, MongoQuery } from '@casl/ability';
import { AppSubject } from '../types/auth';
import { AppAction } from '@/types/shared-enums.generated';
import { useAuth } from './AuthContext';

type AppAbility = Ability<[AppAction, AppSubject], MongoQuery>;

interface AbilityContextType {
  ability: AppAbility;
}

const AbilityContext = createContext<AbilityContextType | undefined>(undefined);

interface AbilityProviderProps {
  children: ReactNode;
}

export const AbilityProvider: React.FC<AbilityProviderProps> = ({ children }) => {
  const { user } = useAuth();

  const ability = useMemo<AppAbility>(() => {
    return defineAbility((can) => {
      if (!user?.permissions?.permissions) {
        return;
      }

      // Construir regras a partir das permissões recebidas do backend
      for (const permission of user.permissions.permissions) {
        const [action, subject] = permission.split(':') as [AppAction, AppSubject];
        if (action && subject) {
          can(action, subject);
        }
      }
    }) as AppAbility;
  }, [user?.permissions?.permissions]);

  return (
    <AbilityContext.Provider value={{ ability }}>
      {children}
    </AbilityContext.Provider>
  );
};

export const useAbility = (): AbilityContextType => {
  const context = useContext(AbilityContext);
  if (context === undefined) {
    throw new Error('useAbility deve ser usado dentro de um AbilityProvider');
  }
  return context;
};

